import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './EditLessonContent.module.scss';
import Button from '../../../../../components/button/Button';
import { useEditUnit } from '../useEditUnit';
import { isEmptyObject, validateIsEmpty } from '../../../../../utils/validation';
import DeleteModal from '../../delete-modal/DeleteModal';
import {
  useUpdateLessonMutation,
  useHeaUpdateLessonMutation,
  useDeleteLessonMutation,
  HeaLessonFragment,
  useIsValidLessonUniqueNameLazyQuery
} from '@api/graphql-codegen';
import { useQueryDictionary } from '@hooks/useQueryDictionary';
import { useRolesPermissions } from '@hooks/useRolesPermission';
import { Lesson, UserRoleEnum } from '@types';
import LessonTabContent, { EditLessonType } from './LessonTabContent';
import { Tabs } from 'antd';
import { logger } from '../../../../../utils/logger';

type Props = {
  refetchUnit: () => void
};

const emptyErrors = {
  name: '',
  uniqueName: ''
};

type ErrorKeys = keyof typeof emptyErrors;

export enum TabKeys {
  Lesson = "Lesson",
  HomeEd = "Home Ed"
}

const defaultLesson: Lesson = {
  id: '',
  name: '',
  duration: 25,
};

function EditLessonContent(props: Props) {
  const { refetchUnit } = props
  const history = useHistory();
  const { lesson, setLesson, setContent, setSelectionLevel, heaUnit, unit } = useEditUnit();

  const isContentAdmin = useRolesPermissions([UserRoleEnum.ContentAdmin]);

  const groups = useQueryDictionary({ dictionary: 'ProgramTypes' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<ErrorKeys, string>>>(emptyErrors);
  const [heaErrors, setHeaErrors] = useState<Partial<Record<ErrorKeys, string>>>(emptyErrors);
  const [lessonContent, setLessonContent] = useState<Lesson | undefined>();
  const [heaLessonContent, setHeaLessonContent] = useState<HeaLessonFragment | undefined>();
  const [currentTab, setCurrentTab] = useState<string>(TabKeys.Lesson);

  const hasHeaUnit = useMemo(() => !!heaUnit, [heaUnit]);

  const [isLessonUnique] = useIsValidLessonUniqueNameLazyQuery({ fetchPolicy: 'network-only'});

  useEffect(() => {
    if (lesson) {
      if (lesson.__typename === 'Lesson') {
        setLessonContent({...lesson})
      }
      if (lesson?.id && heaUnit?.id) {
        const heaLesson = (heaUnit.classPresentation || []).find(cp => cp.originalLessonId === lesson?.id);
        if (heaLesson) {
          setHeaLessonContent(heaLesson)
        }
      }
    } else {
      setLessonContent(defaultLesson)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heaUnit?.id, lesson]);

  const isEdit = !!lesson;

  const [updateLesson, { loading }] = useUpdateLessonMutation({
    update(cache, res) {
      const l = res.data?.updateLesson;
      if (l) {
        cache.modify({
          id: `Lesson:${lesson?.id || ''}`,
          fields: {
            name: () => l.name || '',
            description: () => l.description || '',
            image: () => l.image || '',
            duration: () => +(lessonContent?.duration ?? 25),
            descriptionHeading: () => l.descriptionHeading || 'This lesson helps students:',
            descriptionAnswers: () => l.descriptionAnswers || null,
            prerequisite: () => l.prerequisite  || null,
            group: () => l.group || null,
            sensitive: () => l.sensitive || false,
            sensitiveTooltip: () => l.sensitiveTooltip || 'This Lesson contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.',
            alternativeActivated: () => l.alternativeActivated || false
          }
        });
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    }
  });

  const [updateHeaLesson, { loading: heaLoading }] = useHeaUpdateLessonMutation({
    update(cache, res) {
      const l = res.data?.heaUpdateLesson;
      if (l) {
        cache.modify({
          id: `HEALesson:${l?.id || ''}`,
          fields: {
            name: () => l.name || '',
            description: () => l.description || '',
            image: () => l.image || '',
            number: () => l.number || lesson?.number || null,
            duration: () => +(heaLessonContent?.duration ?? 25),
            descriptionHeading: () => l.descriptionHeading || 'This lesson helps students:',
            descriptionAnswers: () => l.descriptionAnswers || null,
            prerequisite: () => l.prerequisite || null,
            modules: () => l.modules || null,
            sensitive: () => l.sensitive || null,
            sensitiveTooltip: () => l.sensitiveTooltip || 'This Lesson contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.'
          }
        });
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
    }
  });

  const [deleteLesson, {loading: removeLoading}] = useDeleteLessonMutation({
    update() {
      refetchUnit();
    },
    onCompleted(res) {
      setShowDeleteModal(false)
      if (res.deleteLesson) {
        setLesson(undefined);
        setSelectionLevel('Unit');
        setContent(undefined);
      }
    },
  });

  const onChangeUnitContent = useCallback((content: Partial<EditLessonType>) => {
    setLessonContent(data => ({ ...data, ...content } as Lesson));
  }, []);

  const onChangeHeaUnitContent = useCallback((content: Partial<EditLessonType>) => {
    setHeaLessonContent(data => ({ ...data, ...content } as HeaLessonFragment));
  }, []);

  const onChangeTab = useCallback((tabKey: string) => {
    setCurrentTab(tabKey);
  }, []);

  const validate = useCallback(async (content: EditLessonType) => {
    const error: Partial<Record<ErrorKeys, string>> = {};

    if (validateIsEmpty(content.name)) {
      error.name = `Lesson Name can't be empty.`;
    }

    if (content?.uniqueName) {
      const data = (await isLessonUnique({
        variables: {
          input: {
            id: content.id,
            uniqueName: content.uniqueName
          }
        }
      }));

      if (!data.data?.isValidLessonUniqueName) {
        error.uniqueName = `The same lesson name exists.`;
      }
    }

    return error;
  }, [isLessonUnique])

  const isHomeEdTabSelected = useMemo(() => currentTab === 'Home Ed', [currentTab]);

  const handleSave = async () => {
    if (isHomeEdTabSelected) {
      const errors = await validate(heaLessonContent!);
      const descHeading = heaLessonContent?.descriptionHeading === "" ? 'This lesson helps students:' : heaLessonContent?.descriptionHeading

      if (isEmptyObject(errors)) {
        updateHeaLesson({
          variables: {
            input: {
              id: heaLessonContent!.id,
              name: heaLessonContent?.name || "",
              description: heaLessonContent?.description,
              image: heaLessonContent?.image,
              duration: +(heaLessonContent?.duration ?? 25),
              descriptionHeading: descHeading,
              descriptionAnswers:  heaLessonContent?.descriptionAnswers,
              prerequisite: heaLessonContent?.prerequisite,
              sensitive: heaLessonContent?.sensitive,
              sensitiveTooltip: heaLessonContent?.sensitiveTooltip || 'This Lesson contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.',
              group: heaLessonContent?.group,
            }
          }
        });
      } else {
        setErrors(errors);
      }
    } else {
      const errors = await validate(lessonContent!);
      const descHeading = lessonContent?.descriptionHeading === "" ? 'This lesson helps students:' : lessonContent?.descriptionHeading

      if (isEmptyObject(errors)) {
        updateLesson({
          variables: {
            input: {
              id: lessonContent!.id,
              name: lessonContent?.name || "",
              uniqueName: lessonContent?.uniqueName || "",
              description: lessonContent?.description,
              image: lessonContent?.image,
              duration: +(lessonContent?.duration ?? 25),
              descriptionHeading: descHeading,
              descriptionAnswers:  lessonContent?.descriptionAnswers,
              prerequisite: lessonContent?.prerequisite,
              group: lessonContent?.group || undefined,
              sensitive: lessonContent?.sensitive,
              sensitiveTooltip: lessonContent?.sensitiveTooltip || 'This Lesson contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.',
              alternativeActivated: lessonContent?.alternativeActivated
            }
          }
        });
      } else {
        setErrors(errors);
      }
    }
  };

  const handleCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleOpenModal = () => setShowDeleteModal(true);
  const handleCloseModal = () => setShowDeleteModal(false);

  const handleLessonDelete = useCallback(() => {
    lesson && deleteLesson({
      variables: {
        input: { id: lesson.id },
      },
    });
  },[lesson, deleteLesson])

  const tabItems = useMemo(() => hasHeaUnit ? [{
    label: TabKeys.Lesson,
    key: TabKeys.Lesson,
    children: lessonContent ?
    <LessonTabContent
      lesson={lessonContent}
      onChange={onChangeUnitContent}
      groups={groups}
      setErrors={setErrors}
      errors={errors}
    /> : null,
    disabled: loading || removeLoading || heaLoading,
  }, {
    label: TabKeys.HomeEd,
    key: TabKeys.HomeEd,
    children: heaLessonContent ?
    <LessonTabContent
      lesson={heaLessonContent}
      onChange={onChangeHeaUnitContent}
      groups={groups}
      setErrors={setHeaErrors}
      errors={heaErrors}
      isHomeEd
    /> : null,
    disabled: loading || removeLoading || heaLoading,
  }] : [{
    label: TabKeys.Lesson,
    key: TabKeys.Lesson,
    children: lessonContent ?
    <LessonTabContent
      lesson={lessonContent}
      onChange={onChangeUnitContent}
      groups={groups}
      setErrors={setErrors}
      errors={errors}
      unitName={unit?.uniqueName || unit?.name}
    /> : null,
    disabled: loading || removeLoading || heaLoading,
  }], [hasHeaUnit, lessonContent, onChangeUnitContent, groups, errors, loading, removeLoading, heaLoading, heaLessonContent, onChangeHeaUnitContent, heaErrors, unit]);

  return (
    <>
      <div className={styles.row}>
        <div className={styles.actions}>
          <div className={styles.title}>
            Edit lesson
          </div>
          <div className={styles.buttons}>
            <Button className={styles.saveButton} disabled={loading || heaLoading} onClick={handleSave} size={'small'} label={'SAVE'}/>
            <Button className={styles.cancelButton} disabled={loading || heaLoading} onClick={handleCancel} size={'small'} label={'Cancel'}/>
          </div>
        </div>
      </div>
      <Tabs tabBarStyle={{ justifyContent: 'space-between' }} className={styles.tabs} items={tabItems} onChange={onChangeTab} />
      {isEdit &&
        <div className={styles.deleteRow}>
          <div className={styles.subTitle}>Warning</div>
            <Button className={styles.deleteButton} disabled={loading || heaLoading || isContentAdmin} onClick={handleOpenModal} size={'small'} label={'DELETE LESSON'}/>
        </div>}
      {(showDeleteModal && !isHomeEdTabSelected) ? <DeleteModal open={showDeleteModal} loading={removeLoading} onClose={handleCloseModal} onDelete={handleLessonDelete} lesson={lesson}/> : null}
    </>
  );
}

export default EditLessonContent;
